import axios from '@/libs/axios';
import { AvatarOption } from '@/helpers/constants/chat';
import { Logger } from './analytics';
import { UserProfile } from '@/app/api/user/types';
import { flags, year_sessions } from '@prisma/client';
import { Rank } from '@/app/api/student-progress/types';
import { NotesType } from '@/app/api/admin/intervention/types';
import type { CompletedStudentGoal } from '@/helpers/student-goals';

export const createOrUpdateUser = async (): Promise<{
  success: boolean;
  user: UserProfile;
}> => {
  try {
    const domain = window?.location?.origin;
    const response = await axios.post(`/api/user`, { domain });
    return response?.data;
  } catch (error) {
    Logger({
      message: 'Error create or update user',
      error
    });
    throw error;
  }
};

export const createUser = async ({
  name,
  email,
  role,
  org_id
}: {
  name: string;
  email: string;
  role: string;
  org_id: number;
}): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const response = await axios.post(`/api/admin/user/create`, {
      name,
      email,
      role,
      org_id
    });
    return response?.data;
  } catch (error) {
    Logger({
      message: 'Error create user',
      error
    });
    throw error;
  }
};

export const fetchUserProfile = async (): Promise<UserProfile> => {
  try {
    const response = await axios.get('/api/user');
    return response.data.user;
  } catch (error) {
    Logger({
      message: 'Error fetching user profile',
      error
    });
    throw error;
  }
};

export const searchUserEmails = async (str: string) => {
  try {
    if (str === '' || str === null) return [];
    const response = await axios.post('/api/user/autocomplete', {
      value: str
    });
    return response.data.emails;
  } catch (error) {
    Logger({
      message: 'Error search user email',
      error
    });
    throw error;
  }
};

export const getUserAvatar = async (): Promise<{
  selectedAvatar: AvatarOption;
}> => {
  try {
    const response = await axios.get('/api/user/avatar');
    return response.data;
  } catch (error) {
    Logger({
      message: 'Error get user avatar',
      error
    });
    throw error;
  }
};

export const updateUserAvatar = async (avatarId: string): Promise<{ selectedAvatar: AvatarOption }> => {
  try {
    const response = await axios.post('/api/user/avatar', {
      avatarId
    });
    return response.data;
  } catch (error) {
    Logger({
      message: 'Error update user avatar',
      error
    });
    throw error;
  }
};

export async function refreshToken(baseUrl: string, refreshToken: string) {
  try {
    const response = await axios.post(baseUrl + '/api/oauth/refresh', {
      refresh_token: refreshToken
    });
    return response.data;
  } catch (error) {
    console.log(error);
    Logger({
      message: 'Error to update access token',
      error
    });
    throw error;
  }
}

export async function getUserSignInUrl(options?: { forceConsent?: boolean }) {
  try {
    const response = await axios.post('/api/oauth/sign-in', options);
    return response.data;
  } catch (error) {
    console.log(error);
    Logger({
      message: 'Error to get user sign in url',
      error
    });
    throw error;
  }
}

export async function getGuardianSignInUrl(options?: { forceConsent?: boolean }) {
  try {
    const response = await axios.post('/api/oauth/guardian/sign-in', options);
    return response.data;
  } catch (error) {
    console.log(error);
    Logger({
      message: 'Error to get guardian sign in url',
      error
    });
    throw error;
  }
}

export async function signOut() {
  try {
    const response = await axios.post('/api/user/sign-out');
    return response.data;
  } catch (error) {
    console.log(error);
    Logger({
      message: 'Error to sign out',
      error
    });
    throw error;
  }
}

export async function getUserProgress(today: string) {
  try {
    const response = await axios.get('/api/student-progress', {
      params: {
        today
      }
    });
    return response.data;
  } catch (error) {
    Logger({
      message: error.response.error,
      error
    });
    throw error;
  }
}

export async function getUserGuideMessage(today: string, type?: NotesType) {
  try {
    const response = await axios.get('/api/notes', {
      params: {
        today,
        type: type ?? NotesType.MESSAGE
      }
    });
    return response.data;
  } catch (error) {
    Logger({
      message: error.response.error,
      error
    });
    throw error;
  }
}

export async function getUserStudentGoal() {
  try {
    const response = await axios.get('/api/user/student-goal');
    return response.data;
  } catch (error) {
    Logger({
      message: error.response.error,
      error
    });
    throw error;
  }
}

export async function getUserCompletedGoals(): Promise<{
  success: boolean;
  completedGoals: CompletedStudentGoal[];
  inProgressCount: number;
}> {
  try {
    const response = await axios.get('/api/user/student-goal/completed');
    return response.data;
  } catch (error) {
    Logger({
      message: 'Error fetching completed goals',
      error
    });
    throw error;
  }
}

export const getUserFlag = async (date?: string): Promise<{ flag: flags }> => {
  try {
    const response = await axios.get('/api/user/flag', {
      params: date ? { date } : undefined
    });
    return response.data;
  } catch (error) {
    Logger({
      message: 'Error fetching user flag',
      error
    });
    throw error;
  }
};

export const getUserSchedule = async () => {
  try {
    const response = await axios.get('/api/user/schedule');
    return response.data;
  } catch (error) {
    Logger({
      message: 'Error fetching user schedule',
      error
    });
    throw error;
  }
};

export const getUserLeaderboard = async (): Promise<{ rank: Rank; success: boolean; session: year_sessions }> => {
  try {
    const response = await axios.get('/api/user/leaderboard');
    return response.data;
  } catch (error) {
    Logger({
      message: 'Error fetching user leaderboard',
      error
    });
    throw error;
  }
};
